import { AfterViewInit, Component, HostListener, Inject, OnDestroy } from '@angular/core';
import { DOCUMENT }                                                  from '@angular/common';
import { FormBuilder, FormGroup, Validators }                        from '@angular/forms';
import { ActivatedRoute, Router }                                    from '@angular/router';
import { ESCAPE }                                                    from '@angular/cdk/keycodes';
import { HttpErrorResponse }                                         from '@angular/common/http';

import { Observable, Subject, throwError }    from 'rxjs';
import { catchError, filter, map, takeUntil } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import { emailValidatorRegex }   from '@utils/validators/email-validator-regex';
import { RED_LINKS }             from '@shared/menus/links';
import { animationStates, fade } from '@shared/animations/animations';
import { MatchFields }           from '@utils/validators/match-fields.validator';

import { schoolYear, schoolYearOptions } from '@models/utils/school-year';
import { UF, ufOptions }                 from '@models/utils/uf';
import { AuthUser }                      from '@models/user/auth-user';
import { AuthParams }                    from '@models/user/auth-params';
import { Anchor }                        from '@models/navigation/anchor';

import { ErrorService }   from '@utils/error/error.service';
import { OverlayService } from '@services/overlay.service';
import { UserService }    from '@services/user.service';
import { ApiUserService } from '@services/api-user.service';


@Component({
  selector:    'red-login-overlay',
  templateUrl: './auth-overlay.component.html',
  styleUrls:   [ './auth-overlay.component.scss' ],
  animations:  [ fade ]
})
export class AuthOverlayComponent implements AfterViewInit, OnDestroy {

  // private activeUser$: Observable<AuthUser>;
  private unsubscribe$: Subject<void>;

  public document:           Document;
  public authForm:           FormGroup;
  public signUpForm:         FormGroup;
  public passRequestForm:    FormGroup;
  public passResetForm:      FormGroup;
  public formAnimationState: animationStates;
  public schoolYearOptions:  schoolYear[];
  public ufOptions:          UF[];
  public termsOfService:     Anchor;
  public passwordChange:     boolean;
  public signUp:             boolean;
  public updatePass:         boolean;
  public token:              string;
  public userId:             number;

  @HostListener('document:keydown', [ '$event' ])
  public handleKeydown(event: KeyboardEvent) {
    // noinspection JSDeprecatedSymbols
    if (event.keyCode === ESCAPE) this.close();
  }

  constructor(
    private router:         Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder:    FormBuilder,
    private toastr:         ToastrService,
    private errorService:   ErrorService,
    private overlayService: OverlayService,
    private apiUserService: ApiUserService,
    private userService:    UserService,

    @Inject(DOCUMENT) private _document: any
  ) {
    this.unsubscribe$ = new Subject<void>();
    // this.activeUser$  = this.userService.getActiveUser();

    this.document           = this._document as Document;
    this.schoolYearOptions  = schoolYearOptions;
    this.ufOptions          = ufOptions;
    this.termsOfService     = RED_LINKS['terms-of-service'];
    this.formAnimationState = 'void';
    this.signUp             = false;
    this.updatePass         = false;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.formAnimationState = 'enter';
      this.initQueryParams();
      this.initForms();
      this.autofillUserIfLogged();
    }, 0);
  }
  private autofillUserIfLogged() {
    if(!this.passwordChange) return; 
    if(this.updatePass){
      this.apiUserService.getCurrentUserForEdit()
      .pipe(
        takeUntil(this.unsubscribe$), 
        filter(user => !!user && !!user.email ),
        map(user => user.email))
      .subscribe(emailUser => {
        this.passResetForm.get('email').patchValue(emailUser);
        this.passResetForm.get('email').disable();
      })
    } 

    /*this.activeUser$.pipe(
      takeUntil(this.unsubscribe$),
      filter(user => !!user)
    ).subscribe(() => {
      const legacyUser: LegacyUser = this.userService.legacyUser;

      this.passRequestForm.get('email').patchValue(legacyUser && legacyUser.email);
      this.passResetForm.get('email').patchValue(legacyUser && legacyUser.email); */
  }

  private initQueryParams() {
    this.activatedRoute.queryParamMap.subscribe(queryParamMap => {
      this.userId     = +queryParamMap.get('usuarioId');
      this.token      = queryParamMap.get('numToken');
      this.updatePass = queryParamMap.has('trocarSenha');

      this.passwordChange = !!(this.userId && this.token) || this.updatePass;
    });
  }

  private initForms() {
    const emailValidators = [ Validators.required, Validators.pattern(emailValidatorRegex) ];

    this.authForm = this.formBuilder.group(
      {
        login: this.formBuilder.control('', [ ...emailValidators ]),
        senha: this.formBuilder.control('', [ Validators.required ])
      }
    );

    this.signUpForm = this.formBuilder.group(
      {
        nome:             this.formBuilder.control('', [ Validators.required ]),
        email:            this.formBuilder.control('', [ ...emailValidators ]),
        apelido:          this.formBuilder.control('', [ Validators.required ]),
        anoEscolar:       this.formBuilder.control('', [ Validators.required ]),
        uf:               this.formBuilder.control('', [ Validators.required ]),
        municipio:        this.formBuilder.control('', [ Validators.required ]),
        senha:            this.formBuilder.control('', [ Validators.required ]),
        confirmacaoSenha: this.formBuilder.control('', [ Validators.required ])
      }, { validators: MatchFields('senha', 'confirmacaoSenha') }
    );

    this.signUpForm.get('uf').setValue('SP');

    this.passRequestForm = this.formBuilder.group(
      { email: this.formBuilder.control('', [ ...emailValidators ]) }
    );

    this.passResetForm = this.formBuilder.group(
      {
        email:            this.formBuilder.control('', [ ...emailValidators ]),
        senha:            this.formBuilder.control('', [ Validators.required ]),
        confirmacaoSenha: this.formBuilder.control('', [ Validators.required ])
      }, { validators: MatchFields('senha', 'confirmacaoSenha') }
    );
  }

  private handleError(error: HttpErrorResponse): Observable<void> {
    if (error.status === 403) {
      this.toastr.error(
        'Verifique seus dados e tente novamente',
        'Email ou senha inválidos'
      );
    } else {
      this.errorService.displayErrorToast(error);
    }

    if (this.authForm.disabled)        this.authForm.enable();
    if (this.signUpForm.disabled)      this.signUpForm.enable();
    if (this.passRequestForm.disabled) this.passRequestForm.enable();
    if (this.passResetForm.disabled)   this.passResetForm.enable();

    return throwError(() => error);
  }

  public togglePasswordRequest(event?: Event) {

    if (event) event.preventDefault();

    const email = this.authForm.get('login').value;
    this.passRequestForm.get('email').setValue(email);

    if (this.userId || this.token) {
      this.userId = null;
      this.token  = null;
    }

    this.passwordChange = !this.passwordChange;
  }

  public toggleSignUp(event?: Event) {
    if (event) event.preventDefault();

    this.signUp = !this.signUp;
  }

  public submitPasswordChangeRequest() {
    if (this.passRequestForm.invalid) return;

    this.passRequestForm.disable();

    const email = this.passRequestForm.get('email').value;

    this.apiUserService.requestPasswordChange(email).pipe(
      catchError(error => this.handleError(error))
    ).subscribe(() => {
      const message = `O link para a alteração de sua senha foi enviado,
      e deverá chegar em sua caixa de entrada em alguns instantes.`;

      this.toastr.success(message, 'Email enviado');
      this.passRequestForm.enable();
    });
  }

  public submitPasswordReset() {
    if (this.passResetForm.invalid) return;

    this.passResetForm.disable();

    const params = {
      ...this.passResetForm.getRawValue(),
      token:     this.token,
      usuarioId: this.userId
    };

    this.apiUserService.changePassword(params).pipe(
      catchError(error => this.handleError(error))
    ).subscribe(() => {
      const message = `Volte para o login e entre com sua nova senha.`;
      this.toastr.success(message, 'Senha alterada');
      this.passResetForm.enable();

      this.router.navigate(
        [ '/home' ], {
          queryParams: { entrar: true, trocarSenha: null }
        }
      ).then(() => this.updatePass = false);
    });
  }

  public submitAuthentication(value?: AuthParams) {
    if (this.authForm.invalid && !value) return;

    this.authForm.disable();

    this.apiUserService.authenticate(value || this.authForm.value).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error))
    ).subscribe((user: AuthUser) => {
      this.authForm.enable();
      this.userService.updateActiveUser(user);

      this.router.navigate(this.userService.redirectRoute, {
        queryParams: { entrar: null }
      }).then(() => {
        this.formAnimationState = 'leave';
        this.formAnimationDone();
      });
    });
  }

  public submitSignUp() {
    if (this.signUpForm.invalid) return;

    const loginCredentials: AuthParams = {
      senha: this.signUpForm.get('senha').value,
      login: this.signUpForm.get('email').value
    };

    this.signUpForm.disable();

    this.apiUserService.signUpStudent(this.signUpForm.value).pipe(
      catchError((error) => this.handleError(error))
    ).subscribe(() => {
      this.signUpForm.enable();
      this.submitAuthentication(loginCredentials);
    });
  }

  public close() {
    if (this.updatePass) return;

    this.router.navigate([ '.' ], {
      queryParams: { entrar: null }
    }).then(() => {
      this.formAnimationState = 'leave';
      this.formAnimationDone();
    });
  }

  public formAnimationDone() {
    if (this.formAnimationState === 'leave') {
      this.formAnimationState = 'void';
      this.overlayService.close('auth');
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
